import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DataTable, BaseRunners, dateTranslate, columnTypes as colType } from 'yankees-react-utils';
import VideoPlayerButton from '../../../../components/VideoPlayerButton';

const pitchoutLogColumns = (translate) => [
    { id: 'videoplaylist', title: null, type: colType.NODE },
    { id: 'date', title: translate('common.date'), type: colType.TEXT },
    { id: 'att', title: translate('sections.opponent.team.abbr-att'), type: colType.TEXT },
    { id: 'outcome', title: translate('sections.opponent.team.outcome'), type: colType.TEXT },
    { id: 'count', title: translate('baseball.count'), type: colType.TEXT },
    { id: 'outs', title: translate('baseball.outs_other'), type: colType.TEXT },
    { id: 'inn', title: translate('baseball.abbr-inning'), type: colType.TEXT },
    { id: 'score', title: translate('baseball.score'), type: colType.TEXT },
    { id: 'runnerbases', title: translate('sections.opponent.team.runners-on'), type: colType.TEXT },
    { id: 'runners', title: translate('baseball.runners_other'), type: colType.TEXT },
    { id: 'batter', title: translate('baseball.batter'), type: colType.TEXT },
    { id: 'pitcher', title: translate('baseball.pitcher_one'), type: colType.TEXT },
    { id: 'catcher', title: translate('baseball.catcher_one'), type: colType.TEXT },
];

const formatPitchoutLogRows = (translate, rows) => (
    rows.map((r) => {
        const runners = [];
        if (r.runner1id) runners.push(`${r.runner1_lastname} ${r.runner1_usesname?.length && r.runner1_usesname.charAt(0)}`);
        if (r.runner2id) runners.push(`${r.runner2_lastname} ${r.runner2_usesname?.length && r.runner2_usesname.charAt(0)}`);
        if (r.runner3id) runners.push(`${r.runner3_lastname} ${r.runner3_usesname?.length && r.runner3_usesname.charAt(0)}`);
        const videoparams = { pitcherid: r.pitcherid, gameid: r.gameid, eventseq: r.eventseq, pitchseq: r.pitchseq };
        const videoplaylist = (<VideoPlayerButton {...videoparams} />);
        return {
            videoplaylist: !window.$HideVideoComponents ? videoplaylist : [],
            date: dateTranslate(translate, r.gamedate),
            att: r.att,
            outcome: r.outcome,
            count: `${r.balls}-${r.strikes}`,
            outs: r.outs,
            inn: r.inning,
            score: `${r.scorebatting}-${r.scorefielding}`,
            runnerbases: (<BaseRunners baseState={r.runners} emptyColor="#98CBFF" height={20} width={30} />),
            runners: runners.join(', '),
            batter: r.batter_lastname ? `${r.batter_lastname} ${r.batter_usesname?.length && r.batter_usesname.charAt(0)}` : '',
            pitcher: r.pitcher_lastname ? `${r.pitcher_lastname} ${r.pitcher_usesname?.length && r.pitcher_usesname.charAt(0)}` : '',
            catcher: r.catcher_lastname ? `${r.catcher_lastname} ${r.catcher_usesname?.length && r.catcher_usesname.charAt(0)}` : '',
        };
    })
);

const cumulativePitchoutColumns = (translate) => [
    { id: 'total', title: translate('sections.opponent.team.total'), type: colType.TEXT },
    { id: 'sb', title: translate('baseball.abbr-stolen-base'), type: colType.TEXT },
    { id: 'cs', title: translate('baseball.abbr-caught-stealing'), type: colType.TEXT },
    { id: 'r1', title: '1st', type: colType.TEXT },
    { id: 'r12', title: '1st & 2nd', type: colType.TEXT },
    { id: 'r13', title: '1st & 3rd', type: colType.TEXT },
    { id: 'r3', title: '3rd', type: colType.TEXT },
    { id: 'count00', title: '0-0', type: colType.TEXT },
    { id: 'count10', title: '1-0', type: colType.TEXT },
    { id: 'count11', title: '1-1', type: colType.TEXT },
    { id: 'count12', title: '1-2', type: colType.TEXT },
    { id: 'count20', title: '2-0', type: colType.TEXT },
    { id: 'count21', title: '2-1', type: colType.TEXT },
    { id: 'count22', title: '2-2', type: colType.TEXT },
    { id: 'count30', title: '3-0', type: colType.TEXT },
    { id: 'count31', title: '3-1', type: colType.TEXT },
    { id: 'count32', title: '3-2', type: colType.TEXT },
];

const formatCumulativePitchoutRows = (pitchoutByCount, pitchoutSummary) => {
    const summary = pitchoutSummary.length ? pitchoutSummary[0] : {};
    return [{
        total: summary.po,
        sb: summary.sb,
        cs: summary.cs,
        r1: summary.r1,
        r12: summary.r12,
        r13: summary.r13,
        r3: summary.r3,
        count00: pitchoutByCount.find((c) => c.balls === 0 && c.strikes === 0).po,
        count10: pitchoutByCount.find((c) => c.balls === 1 && c.strikes === 0).po,
        count11: pitchoutByCount.find((c) => c.balls === 1 && c.strikes === 1).po,
        count12: pitchoutByCount.find((c) => c.balls === 1 && c.strikes === 2).po,
        count20: pitchoutByCount.find((c) => c.balls === 2 && c.strikes === 0).po,
        count21: pitchoutByCount.find((c) => c.balls === 2 && c.strikes === 1).po,
        count22: pitchoutByCount.find((c) => c.balls === 2 && c.strikes === 2).po,
        count30: pitchoutByCount.find((c) => c.balls === 3 && c.strikes === 0).po,
        count31: pitchoutByCount.find((c) => c.balls === 3 && c.strikes === 1).po,
        count32: pitchoutByCount.find((c) => c.balls === 3 && c.strikes === 2).po,
    }];
};

const StealingAgainstPitchouts = ({ data }) => {
    const { t: translate } = useTranslation();
    const { pitchoutlog, pitchoutbycount, pitchoutsummary } = data;
    const pitchoutLogRows = formatPitchoutLogRows(translate, pitchoutlog);
    const cumulativePitchoutRows = formatCumulativePitchoutRows(pitchoutbycount, pitchoutsummary);
    return !!data && !!data.pitchoutlog && (
        <>
            <DataTable title={translate('sections.opponent.team.pitchout-log')} columns={pitchoutLogColumns(translate)} rows={pitchoutLogRows} />
            <br />
            <DataTable title={translate('sections.opponent.team.cumulative-pitchout-statistics')} columns={cumulativePitchoutColumns(translate)} rows={cumulativePitchoutRows} />
        </>
    );
};

StealingAgainstPitchouts.propTypes = {
    data: PropTypes.shape({
        pitchoutlog: PropTypes.arrayOf(PropTypes.object),
        pitchoutbycount: PropTypes.arrayOf(PropTypes.object),
        pitchoutsummary: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
};

export default StealingAgainstPitchouts;
